import React, { forwardRef, ReactElement, useImperativeHandle, useState, useEffect } from 'react';
import Draggable from 'react-draggable';

import classes from './answerElement.module.scss';

import GrabSVG from '@/assets/svg/grab.svg';
import { useStorage } from '@/models/Storage';
import useMobile from '@/hooks/useMobile';

export type AnswerProps = {
    id: string;
    icon: ReactElement<any, any>;
    text: string;
    onDrop: (id?: string) => void;
    onDrag: (id: string) => void;
    unlock: (id: string) => void;
    index: number;
};

const offset = 150;

export const AnswerElement = forwardRef(
    ({ id, icon, text, onDrop, onDrag, unlock, index }: AnswerProps, ref) => {
        const mobile = useMobile();
        const [isDragged, setIsDragged] = useState(false);
        const [error, setError] = useState(false);
        const [success, setSuccess] = useState(false);
        const [hide, setHide] = useState(false);
        const [animated, setAnimated] = useState(false);
        const [locked, setLocked] = useState(false);
        const [lastSavedPosition, setLastSavedPosition] = useState({ x: 0, y: 0 });
        const answerRef = React.useRef(null);

        const { guide, finishGuide } = useStorage();

        const dragToArea = () => {
            const area = document.querySelector('#area');
            const areaBox = area.getBoundingClientRect();
            const answerBox = answerRef.current.getBoundingClientRect();
            const targetY = areaBox.y + areaBox.height - 50 - answerBox.height / 2;
            const targetX = areaBox.x + areaBox.width / 2 - answerBox.width / 2;
            const deltaY = Math.abs(answerBox.y - targetY) * (answerBox.y >= targetY ? -1 : 1);
            const deltaX = Math.abs(answerBox.x - targetX) * (answerBox.x >= targetX ? -1 : 1);

            setLastSavedPosition({
                x: lastSavedPosition.x + deltaX,
                y: lastSavedPosition.y + deltaY,
            });
        };

        useEffect(() => {
            if (animated) {
                setLastSavedPosition({ x: 0, y: 0 });
            }
        }, [animated]);

        useEffect(() => {
            if (success) {
                try {
                    navigator?.vibrate([15, 5, 25]);
                    dragToArea();
                } catch (e) {
                    return;
                }
            }
        }, [success]);

        useImperativeHandle(ref, () => ({
            triggerError() {
                setError(true);
                try {
                    navigator?.vibrate(80);
                } catch (e) {
                    return;
                }
                setAnimated(true);
            },
            triggerSuccess() {
                unlock(id);
                setSuccess(true);
            },
            setLock(lock: boolean) {
                setLocked(lock);
            },
        }));

        useEffect(() => {
            let animTimer, resetTimer;
            if (guide && index === 0) {
                animTimer = setTimeout(() => {
                    setIsDragged(true);
                    onDrag(id);
                    dragToArea();
                }, 2500 + (mobile ? 4500 : 0));
                resetTimer = setTimeout(() => {
                    onDrop();
                    setIsDragged(false);
                    setLastSavedPosition({ x: 0, y: 0 });
                    setAnimated(true);
                    finishGuide();
                }, 5000 + (mobile ? 4500 : 0));
            }
            return () => {
                clearTimeout(animTimer);
                clearTimeout(resetTimer);
            };
        }, [index, guide]);

        const checkDroppable = (e, ui) => {
            const droppable = document.querySelector('#area');
            let result = false;
            if (droppable) {
                const boundingClientRect = droppable.getBoundingClientRect();
                const topBorder = boundingClientRect.y - offset;
                const rightBorder = boundingClientRect.x + boundingClientRect.width + offset;
                const bottomBorder = boundingClientRect.y + boundingClientRect.height + offset;
                const leftBorder = boundingClientRect.x - offset;
                const answerBox = answerRef.current.getBoundingClientRect();
                result =
                    answerBox.x >= leftBorder &&
                    answerBox.x <= rightBorder &&
                    answerBox.y >= topBorder &&
                    answerBox.y <= bottomBorder;
            }
            return result;
        };

        return (
            <li
                key={id}
                className={`${animated ? classes.anim : ''} ${classes.li}  ${
                    guide ? classes.guide : ''
                } ${isDragged ? classes.dragged : ''}`}
            >
                <Draggable
                    disabled={locked || success || guide}
                    onDrag={(e) => {
                        if (!isDragged) {
                            setIsDragged(true);
                            onDrag(id);
                        }
                    }}
                    onStop={(e: MouseEvent, ui) => {
                        if (isDragged) {
                            setLastSavedPosition({ x: ui.lastX, y: ui.lastY });
                            if (checkDroppable(e, ui)) {
                                onDrop(id);
                            } else {
                                onDrop();
                                setAnimated(true);
                            }
                        }
                        setIsDragged(false);
                    }}
                    defaultPosition={{ x: 0, y: 0 }}
                    position={success || guide ? lastSavedPosition : { x: 0, y: 0 }}
                    defaultClassNameDragging={classes.dragged}
                >
                    <div
                        ref={answerRef}
                        className={`${classes.answerWrapper} ${hide ? classes.hide : ''}`}
                        onTransitionEnd={(e) => {
                            if (e.propertyName === 'transform' && (error || animated)) {
                                setError(false);
                                setAnimated(false);
                                unlock(id);
                            }
                            if (e.propertyName === 'opacity' && success) {
                                setHide(true);
                            }
                        }}
                    >
                        <div
                            className={`${classes.answer} ${error ? classes.error : ''} ${
                                success ? classes.success : ''
                            }`}
                        >
                            {icon}
                            <span>{text}</span>
                        </div>
                        {guide && index === 0 && <GrabSVG className={classes.grab} />}
                    </div>
                </Draggable>
                <div
                    className={`${classes.placeholder} ${
                        lastSavedPosition.x !== 0 ||
                        lastSavedPosition.y !== 0 ||
                        isDragged ||
                        animated
                            ? classes.visible
                            : ''
                    }`}
                >
                    <div className={`${classes.answer}`}>
                        {icon}
                        <span>{text}</span>
                    </div>
                </div>
            </li>
        );
    },
);

AnswerElement.displayName = 'AnswerElement';
